import React, { FC, Fragment, memo } from 'react';
import { Collapse } from 'reactstrap';
import styled from 'styled-components';
import _isUndefined from 'lodash/isUndefined';
import _isArray from 'lodash/isArray';
import _some from 'lodash/some';
import _every from 'lodash/every';
import _includes from 'lodash/includes';
import _map from 'lodash/map';
import { SelectOption } from '@minecraft.typings';
import { helpers } from '@minecraft.utils';
import { DropdownWithChildrenProps } from '../shared/types';
import DropdownItem from '../DropdownWithChildrenDropdownItem';

const { getAllNestedOptions } = helpers;

const SubDropdownList = styled.div<{ $paddingValue: number }>`
  & .dropdown-item-inner {
    padding-left: ${(props): string => `${props.$paddingValue}rem`};
  }
`;

interface Props extends Partial<DropdownWithChildrenProps> {
  nestingLevel?: number;
  selectedOptions: SelectOption[];
  disabledOptions: SelectOption[];
  collapsedParentOptions?: SelectOption[];
  toggleParentOptions?: (parentItem: SelectOption) => void;
  handleDropdownItemSelect: (newItems: SelectOption[]) => void;
}

const shouldItemBeSelected = (selectedOptions: SelectOption | SelectOption[], item: SelectOption): boolean => {
  const areSubOptionsExist = !_isUndefined(item?.options);
  const allNestedOptions = getAllNestedOptions(item.options);
  const selectedOptionsValue = _map(selectedOptions as SelectOption[], 'value');

  if (areSubOptionsExist && _every(allNestedOptions, (option) => _includes(selectedOptionsValue, option.value))) {
    return true;
  }

  if (
    !areSubOptionsExist &&
    (_some(selectedOptions as SelectOption[], { value: item.value }) ||
      (selectedOptions as SelectOption).value === item.value)
  ) {
    return true;
  }

  return false;
};

const shouldItemBeDisabled = (
  isMultipleSelect: boolean,
  selectionsLimit: number,
  selectedOptions: SelectOption | SelectOption[],
  disabledOptions: SelectOption | SelectOption[],
  item: SelectOption
): boolean => {
  const areSubOptionsExist = !_isUndefined(item?.options);
  const allNestedOptions = getAllNestedOptions(item.options);
  const selectedOptionsValue = _map(selectedOptions as SelectOption[], 'value');
  const disabledOptionsValue = _map(disabledOptions as SelectOption[], 'value');

  if (
    isMultipleSelect &&
    selectionsLimit &&
    selectionsLimit === selectedOptionsValue?.length &&
    !_includes(selectedOptionsValue, item.value)
  ) {
    return true;
  }

  if (areSubOptionsExist && _every(allNestedOptions, (option) => _includes(disabledOptionsValue, option.value))) {
    return true;
  }

  if (
    !areSubOptionsExist &&
    (_some(disabledOptions as SelectOption[], { value: item.value }) ||
      (disabledOptions as SelectOption).value === item.value)
  ) {
    return true;
  }

  return false;
};

const shouldCheckboxBeEnabled = (selectionsLimit: number, nestingLevel: number, item: SelectOption): boolean => {
  const areSubOptionsExist = !_isUndefined(item?.options);

  if (selectionsLimit && areSubOptionsExist) {
    return false;
  }

  return (
    !areSubOptionsExist ||
    (nestingLevel > 1 && _isArray(item?.options) && item?.options?.length > 1) ||
    nestingLevel === 1
  );
};

const shouldMinusBeEnabled = (selectedOptions: SelectOption[], item: SelectOption): boolean => {
  const allNestedOptions = getAllNestedOptions(item.options);
  const selectedOptionsValue = _map(selectedOptions, 'value');

  return _some(allNestedOptions, (option) => _includes(selectedOptionsValue, option.value));
};

const DropdownWithChildrenDropdownList: FC<Props> = ({
  isMultipleSelect,
  isCollapsable,
  selectionsLimit,
  nestingLevel = 1,
  options,
  selectedOptions,
  disabledOptions,
  collapsedParentOptions,
  toggleParentOptions,
  handleDropdownItemSelect,
  dataTestId,
}) => {
  return _map(options, (item) => {
    const areNestedItemsCollapsed = _some(collapsedParentOptions, { value: item.value }) || nestingLevel > 1;
    const areSubOptionsExist = !_isUndefined(item.options);
    const isItemCountExist = !_isUndefined(item.count);
    const isItemDisabled = shouldItemBeDisabled(
      isMultipleSelect,
      selectionsLimit,
      selectedOptions,
      disabledOptions,
      item
    );

    return (
      <Fragment key={item.value || item.label}>
        <DropdownItem
          isMultipleSelect={isMultipleSelect}
          isCollapsable={isCollapsable}
          isItemSelected={shouldItemBeSelected(selectedOptions, item)}
          isItemDisabled={isItemDisabled}
          isItemCountExist={isItemCountExist}
          areSubOptionsExist={areSubOptionsExist}
          checkboxEnable={isMultipleSelect && shouldCheckboxBeEnabled(selectionsLimit, nestingLevel, item)}
          nestingLevel={nestingLevel}
          minusEnable={isMultipleSelect && shouldMinusBeEnabled(selectedOptions, item)}
          item={item}
          onDropdownItemSelect={handleDropdownItemSelect}
          toggleParentOptions={toggleParentOptions}
          dataTestId={dataTestId}
        />
        {item?.options && (
          <Collapse isOpen={!isCollapsable || areNestedItemsCollapsed}>
            <SubDropdownList $paddingValue={1.5 * nestingLevel}>
              <DropdownWithChildrenDropdownList
                isMultipleSelect={isMultipleSelect}
                selectionsLimit={selectionsLimit}
                nestingLevel={nestingLevel + 1}
                options={item?.options}
                selectedOptions={selectedOptions}
                disabledOptions={disabledOptions}
                handleDropdownItemSelect={handleDropdownItemSelect}
                dataTestId={dataTestId}
              />
            </SubDropdownList>
          </Collapse>
        )}
      </Fragment>
    );
  });
};

export default memo(DropdownWithChildrenDropdownList);
